import { type ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { settingsApi } from '@/src/api/adminDataApi';
import { useLanguage } from '@/hooks/useLanguage';
import { SkeletonLayout } from '@/components/ui/Skeleton';

export const isAdminPath = (pathname: string): boolean => {
    const segments = pathname.split('/').filter(Boolean);
    return segments[1] === 'admin';
};

const MaintenanceLoading = () => {
    const { t } = useLanguage();
    return (
        <SkeletonLayout
            label={t('common.loading')}
            className="flex min-h-screen items-center justify-center bg-muted"
        />
    );
};

const MaintenancePage = () => {
    const { language, t } = useLanguage();

    return (
        <main className="flex min-h-screen items-center justify-center bg-muted p-6 text-center">
            <div className="max-w-lg rounded-xl border border-border bg-card p-8 shadow-sm">
                <h1 className={`font-display text-3xl font-semibold text-foreground ${language === 'si' ? 'font-si' : ''}`}>
                    {t('maintenance.title')}
                </h1>
                <p className={`mt-4 text-muted-foreground ${language === 'si' ? 'font-si' : ''}`}>
                    {t('maintenance.description')}
                </p>
            </div>
        </main>
    );
};

export const MaintenanceModeGate = ({ children }: { children: ReactNode }) => {
    const { pathname } = useLocation();
    const [isLoading, setIsLoading] = useState(!isAdminPath(pathname));
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const adminPath = isAdminPath(pathname);

    useEffect(() => {
        if (adminPath) {
            setIsLoading(false);
            return;
        }

        let isCurrent = true;
        setIsLoading(true);
        settingsApi.publicGet()
            .then(response => {
                if (isCurrent) setMaintenanceMode(response.settings.maintenanceMode === true);
            })
            .catch(error => {
                // A settings-read outage must not turn into an accidental site outage.
                console.warn('Failed to load maintenance mode setting; allowing public site access.', error);
                if (isCurrent) setMaintenanceMode(false);
            })
            .finally(() => {
                if (isCurrent) setIsLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [adminPath, pathname]);

    if (adminPath) return <>{children}</>;
    if (isLoading) return <MaintenanceLoading />;
    return maintenanceMode ? <MaintenancePage /> : <>{children}</>;
};
